import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  addDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { db } from "./firebase";
import { getSpotifyUserProfile } from "./spotifyService";

// ─── GET PROFILE ──────────────────────────────────────────────────────────────
export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}

// ─── UPDATE PROFILE ───────────────────────────────────────────────────────────
export async function updateUserProfile(uid, data) {
  await updateDoc(doc(db, "users", uid), { ...data, updatedAt: serverTimestamp() });
}

// ─── UPLOAD PROFILE PICTURE (disabled - requires paid plan) ──────────────────
export async function uploadProfilePicture(uid, file) {
  console.warn("Profile picture upload requires Firebase Storage (paid plan)");
  return null;
}

// ─── PRIVACY SETTINGS ─────────────────────────────────────────────────────────
export async function updatePrivacy(uid, privacy) {
  await updateDoc(doc(db, "users", uid), { privacy });
}

// ─── CONNECT SOCIAL ACCOUNT ───────────────────────────────────────────────────
export async function connectSocialAccount(uid, platform, data) {
  await updateDoc(doc(db, "users", uid), {
    [`connectedAccounts.${platform}`]: data,
  });
}

export async function disconnectSocialAccount(uid, platform) {
  await updateDoc(doc(db, "users", uid), {
    [`connectedAccounts.${platform}`]: deleteField(),
  });
}

// Instagram has no sign-in we can use for personal accounts, so users type their
// handle. It is a public link only and is not verified as belonging to them.
export function parseInstagramUsername(input) {
  let name = (input || "").trim();
  name = name.replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/^@/, "");
  name = name.split(/[/?#]/)[0].trim();
  return /^[A-Za-z0-9._]{1,30}$/.test(name) ? name : null;
}

export async function connectInstagramAccount(uid, input) {
  const username = parseInstagramUsername(input);
  if (!username) return null;
  await connectSocialAccount(uid, "instagram", {
    username,
    displayName: `@${username}`,
    profileUrl: `https://www.instagram.com/${username}/`,
    connected: true,
  });
  return username;
}

// Same idea for X: a typed handle shown as a public link, not verified.
export function parseXUsername(input) {
  let name = (input || "").trim();
  name = name.replace(/^https?:\/\/(www\.|mobile\.)?(x|twitter)\.com\//i, "").replace(/^@/, "");
  name = name.split(/[/?#]/)[0].trim();
  return /^[A-Za-z0-9_]{1,15}$/.test(name) ? name : null;
}

export async function connectXAccount(uid, input) {
  const username = parseXUsername(input);
  if (!username) return null;
  await connectSocialAccount(uid, "x", {
    username,
    displayName: `@${username}`,
    profileUrl: `https://x.com/${username}`,
    connected: true,
  });
  return username;
}

export async function syncSpotifyAccount(uid, token) {
  const spotifyProfile = await getSpotifyUserProfile(token);
  if (spotifyProfile.error) return null;
  await connectSocialAccount(uid, "spotify", {
    id: spotifyProfile.id,
    displayName: spotifyProfile.display_name,
    email: spotifyProfile.email || "",
    profileUrl: spotifyProfile.external_urls?.spotify || "",
    connected: true,
  });
  return spotifyProfile;
}

// ─── SEND CONNECTION REQUEST ──────────────────────────────────────────────────
export async function sendConnectionRequest(fromUid, toUid) {
  const reqRef = doc(db, "connectionRequests", `${fromUid}_${toUid}`);
  await setDoc(reqRef, {
    from: fromUid,
    to: toUid,
    status: "pending",
    createdAt: serverTimestamp(),
  });
}

// ─── ACCEPT CONNECTION ────────────────────────────────────────────────────────
export async function acceptConnection(fromUid, toUid) {
  await deleteDoc(doc(db, "connectionRequests", `${fromUid}_${toUid}`));
  const connId = [fromUid, toUid].sort().join("_");
  await setDoc(doc(db, "connections", connId), {
    users: [fromUid, toUid],
    connectedAt: serverTimestamp(),
  });
}

// ─── DECLINE CONNECTION ───────────────────────────────────────────────────────
export async function declineConnection(fromUid, toUid) {
  await deleteDoc(doc(db, "connectionRequests", `${fromUid}_${toUid}`));
}

// ─── GET CONNECTIONS ──────────────────────────────────────────────────────────
export async function getConnections(uid) {
  const q = query(collection(db, "connections"), where("users", "array-contains", uid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const other = d.data().users.find((u) => u !== uid);
    return { connId: d.id, otherUid: other, ...d.data() };
  });
}

// ─── GET PENDING REQUESTS ─────────────────────────────────────────────────────
export async function getPendingRequests(uid) {
  const q = query(
    collection(db, "connectionRequests"),
    where("to", "==", uid),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── SUBSCRIBE TO CONNECTIONS ─────────────────────────────────────────────────
export function subscribeToConnections(uid, callback) {
  const q = query(collection(db, "connections"), where("users", "array-contains", uid));
  return onSnapshot(q, (snap) => {
    const conns = snap.docs.map((d) => ({
      connId: d.id,
      otherUid: d.data().users.find((u) => u !== uid),
      ...d.data(),
    }));
    callback(conns);
  });
}

// ─── SUBSCRIBE TO PENDING REQUESTS ───────────────────────────────────────────
export function subscribeToPendingRequests(uid, callback) {
  const q = query(
    collection(db, "connectionRequests"),
    where("to", "==", uid),
    where("status", "==", "pending")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ─── CHECK IF CONNECTED ───────────────────────────────────────────────────────
export async function isConnected(uid1, uid2) {
  const connId = [uid1, uid2].sort().join("_");
  const snap = await getDoc(doc(db, "connections", connId));
  return snap.exists();
}

// ─── SEARCH USERS ─────────────────────────────────────────────────────────────
export async function searchUsers(searchTerm) {
  const q = query(
    collection(db, "users"),
    where("privacy", "==", "public"),
    where("displayName", ">=", searchTerm),
    where("displayName", "<=", searchTerm + "\uf8ff"),
    limit(20)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
}

// ─── UPDATE MUSIC METRICS ─────────────────────────────────────────────────────
export async function recordSongRequest(uid, song) {
  const metricsRef = doc(db, "users", uid, "metrics", "songs");
  const snap = await getDoc(metricsRef);
  const current = snap.exists() ? snap.data() : {};
  const key = song.id;
  current[key] = {
    ...song,
    count: (current[key]?.count || 0) + 1,
  };
  await setDoc(metricsRef, current);
}

export async function getUserMetrics(uid) {
  const snap = await getDoc(doc(db, "users", uid, "metrics", "songs"));
  if (!snap.exists()) return { mostRequested: [] };
  const songs = Object.values(snap.data());
  const mostRequested = songs.sort((a, b) => b.count - a.count).slice(0, 5);
  return { mostRequested };
}
